import { useState, FC, ReactNode } from 'react';
import { ToTop } from '../modules';

type SectionProps = {
  title: string;
  children: ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => (
  <section style={{ marginBottom: '2.5rem' }}>
    <h2
      className="privacypolicy_contents_h2"
      style={{
        borderBottom: '2px solid #eee',
        paddingBottom: '0.5rem',
        marginBottom: '1.5rem',
      }}
    >
      {title}
    </h2>
    <div style={{ lineHeight: 1.8, color: '#555' }}>{children}</div>
  </section>
);

type PolicyHeaderProps = {
  title: string;
  subTitle: string;
  lastUpdate: string;
};

const PolicyHeader: FC<PolicyHeaderProps> = ({
  title,
  subTitle,
  lastUpdate,
}) => (
  <header style={{ textAlign: 'center', marginBottom: '4rem' }}>
    <h1 className="privacypolicy_title" style={{ marginBottom: '0.5rem' }}>
      {title}
    </h1>
    <h2
      className="privacypolicy_subTitle"
      style={{ marginBottom: '1rem', color: '#666' }}
    >
      {subTitle}
    </h2>
    <p
      className="privacypolicy_contents_body"
      style={{ color: '#888', fontSize: '0.9rem' }}
    >
      {lastUpdate}
    </p>
  </header>
);

type LangButtonProps = {
  onClick: () => void;
  isActive: boolean;
  children: ReactNode;
  style?: React.CSSProperties;
};

const LangButton: FC<LangButtonProps> = ({
  onClick,
  isActive,
  children,
  style,
}) => (
  <button
    onClick={onClick}
    style={{
      ...style,
      padding: '8px 16px',
      cursor: 'pointer',
      border: isActive ? '1px solid #333' : '1px solid #ccc',
      backgroundColor: isActive ? '#333' : 'white',
      color: isActive ? 'white' : '#333',
      borderRadius: '4px',
      transition: 'all 0.3s',
      minWidth: '110px',
      whiteSpace: 'nowrap',
      height: '40px',
    }}
  >
    {children}
  </button>
);

const EtanPrivacypolicy: FC = () => {
  const [lang, setLang] = useState<'jp' | 'en'>('jp');

  // 日本語コンテンツ
  const jpContent = {
    title: '英たん',
    subTitle: 'プライバシーポリシー',
    lastUpdate: '最終更新日: 2025年11月24日',
    introduction:
      '本プライバシーポリシーは、Takumi Kanaya（以下「当方」）が作成したモバイルデバイス向けアプリケーション「英たん」（以下「本アプリ」）に適用されます。',
    section1Title: '1. 取得するユーザー情報',
    section1Body:
      '本アプリでは、より良い体験を提供するために、特定の個人を識別できる情報（以下「個人情報」）の提供をお願いすることがあります。要求された情報は当方によって保持され、本プライバシーポリシーに記載されているとおりに使用されます。',
    section1_1Title: '1.1. ユーザー認証情報',
    section1_1Content1:
      '本アプリは、複数デバイス間でのデータ同期機能を提供するために、Firebase Authenticationを利用したアカウント登録機能を提供しています。アカウントを作成する際に、以下の情報がGoogle LLCに提供されます。さらに、Firebase Authenticationにより発行される一意のユーザーID（Firebase UID）を取得します。このUIDは、ユーザーを識別し、認証およびデータ同期を安全に行うために使用されます。',
    section1_1Li1:
      'GoogleアカウントまたはAppleアカウントの情報（表示名、メールアドレスなど）',
    section1_2Title: '1.2. 学習データ',
    section1_2Body:
      '本アプリは、ユーザーの学習データを端末内に保存します。アカウントを作成しログインした場合、データ同期を目的として、以下の学習データをFirebase Cloud Firestoreに保存します。',
    section1_2Li1: 'リスニング学習や4択クイズなどの学習履歴',
    section1_2Li2: '各単語の習熟度、正解・不正解の記録',
    section1_2Li3: 'ユーザーが作成した「苦手な単語」リスト',
    section2Title: '2. 情報の利用目的',
    section2Body: '取得した情報は、以下の目的のために利用します。',
    section2_1Li: 'ユーザー認証、学習データの安全な保存および同期',
    section2_2Li:
      '学習進捗の管理、復習機能、クイズ機能など、本アプリの中核機能の提供',
    section2_3Li: '重要な情報、必要な通知、マーケティングプロモーションの提供',
    section2_4Li: 'ユーザーからのお問い合わせへの対応',
    section3Title: '3. 情報の第三者提供',
    section3Body:
      '当方は、本プライバシーステートメントに記載されている方法で、お客様の情報を第三者と共有することがあります。',
    section3_1Li: '法令に基づき開示が求められた場合',
    section3_2Li: '人の生命、身体または財産の保護のために必要がある場合',
    section3_3Li:
      '当方の権利、お客様の安全または他者の安全を守るため、詐欺を調査するため、または政府の要請に応じるために開示が必要であると誠実に信じる場合',
    section3_4Li:
      '当方に代わって業務を行う信頼できるサービス提供者と共有する場合。これらの提供者は、当方が開示した情報を独立して使用することはなく、本プライバシーステートメントに定められた規則を遵守することに同意しています。',
    section3_3Title: '3.1. 外部サービスの利用',
    section3_3Body:
      '本アプリは、機能実現のために以下の外部サービスを利用しており、それに伴いユーザー情報が各事業者に提供されることがあります。詳細については、各社のプライバシーポリシーをご確認ください。',
    section3_3Firebase: 'Firebase (Google LLC)',
    section3_3FirebaseLink: 'https://policies.google.com/privacy',
    section4Title: '4. オプトアウトの権利',
    section4Body:
      '本アプリをアンインストールすることで、すべての情報収集を簡単に停止することができます。お使いのモバイルデバイス、またはモバイルアプリケーションマーケットプレイスやネットワークの一部として利用可能な、標準的なアンインストールプロセスを使用することができます。',
    section5Title: '5. データ保持方針',
    section5Body:
      '当方は、お客様が本アプリを使用している限り、提供されたデータを保持し、その後も合理的な期間保持します。お客様が提供したユーザー提供データの削除を希望される場合は、brownpowder@gmail.com までご連絡ください。合理的な時間内に対応いたします。',
    section6Title: '6. お子様の利用について',
    section6Body:
      '当方は、13歳未満の子供から意図的にデータを募集したり、マーケティングを行ったりすることはありません。保護者および法定後見人の方は、お子様のインターネット利用状況を監視し、本ポリシーを徹底させるため、許可なくアプリケーションやサービスを通じて個人情報を提供しないようお子様に指導することをお勧めします。お子様が個人情報を提供したと思われる場合は、当方が必要な措置を講じられるよう、ご連絡ください。',
    section7Title: '7. セキュリティ',
    section7Body:
      '当方は、お客様の情報の機密性を保護することに関心を持っています。当方が処理・維持する情報を保護するために、物理的、電子的、手続き的な保護措置を提供します。',
    section8Title: '8. 変更',
    section8Body:
      '本プライバシーポリシーは、いかなる理由でも随時更新されることがあります。本プライバシーポリシーの変更は、このページを新しいプライバシーポリシーで更新することによって通知します。継続的な使用はすべての変更を承認したものとみなされるため、変更については定期的に本プライバシーポリシーを参照することをお勧めします。',
    section9Title: '9. お客様の同意',
    section9Body:
      '本アプリを使用することにより、お客様は、本プライバシーポリシーに定められたとおり、現在および当方によって修正されたお客様の情報の処理に同意するものとします。',
    section10Title: '10. 準拠法および管轄裁判所',
    section10Body:
      '本ポリシーは日本法に準拠するものとします。本ポリシーに起因しまたは関連する一切の紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。',
    section11Title: '11. フィードバック機能について',
    section11Body: (
      <>
        <p
          className="privacypolicy_contents_body"
          style={{ marginBottom: '1rem' }}
        >
          本アプリでは、品質向上のため、ユーザーが任意で単語の問題を報告できる機能を提供しています。この機能利用により、以下の情報が収集、送信されます。
        </p>
        <ul
          className="privacypolicy_contents_ul"
          style={{ listStyle: 'disc', paddingLeft: '20px' }}
        >
          <li
            className="privacypolicy_contents_ul_li"
            style={{ marginBottom: '0.5rem' }}
          >
            <strong>収集情報:</strong> 報告対象の単語情報、問題種別、任意入力テキスト、報告日時、アプリバージョン
          </li>
          <li
            className="privacypolicy_contents_ul_li"
            style={{ marginBottom: '0.5rem' }}
          >
            <strong>利用目的:</strong> コンテンツの正確性向上とサービス改善
          </li>
          <li className="privacypolicy_contents_ul_li">
            <strong>情報の送信・保管:</strong> 報告情報はGoogleの「Firebase」(Firestore)に送信・保管されます。Googleのプライバシーポリシーは以下をご参照ください。{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://policies.google.com/privacy
            </a>
          </li>
        </ul>
      </>
    ),
    section12Title: '12. お問い合わせ',
    section12Body:
      '本アプリの利用中にプライバシーに関するご質問がある場合、または当方の慣行についてご質問がある場合は、電子メールにて brownpowder@gmail.com までご連絡ください。',
  };

  // 英語コンテンツ
  const enContent = {
    title: 'Eitan',
    subTitle: 'Privacy Policy',
    lastUpdate: 'Last Updated: November 24, 2025',
    introduction:
      'This privacy policy applies to the 英たん app (hereby referred to as "Application") for mobile devices that was created by Takumi Kanaya (hereby referred to as "Service Provider") as a Freemium service. This service is intended for use "AS IS".',
    section1Title: '1. Information We Collect',
    section1Body:
      'For a better experience, while using the Application, the Service Provider may require you to provide us with certain personally identifiable information. The information that the Service Provider request will be retained by them and used as described in this privacy policy.',
    section1_1Title: '1.1 User Authentication Information',
    section1_1Content1:
      'The Application uses Firebase Authentication to provide account registration for data synchronization across multiple devices. When you create an account, the following information is provided to Google LLC. Additionally, we obtain a unique user ID (Firebase UID) issued by Firebase Authentication. This UID is used to securely identify, authenticate, and synchronize data for the user.',
    section1_1Li1:
      'Information from your Google or Apple account (e.g., display name, email address)',
    section1_2Title: '1.2 Learning Data',
    section1_2Body:
      'The App stores user learning data on the device. If you create an account and log in, the following learning data is stored in Firebase Cloud Firestore for data synchronization purposes:',
    section1_2Li1:
      'Learning history (e.g., listening practice, multiple-choice quizzes)',
    section1_2Li2:
      'Proficiency levels and records of correct/incorrect answers',
    section1_2Li3: 'User-created “difficult words” lists',
    section2Title: '2. How We Use Information',
    section2Body: 'The information is used for the following purposes:',
    section2_1Li: 'User authentication and secure data synchronization',
    section2_2Li:
      'Providing core features of the App, such as progress tracking and review tools',
    section2_3Li:
      'To provide you with important information, required notices and marketing promotions',
    section2_4Li: 'Responding to user inquiries',
    section3Title: '3. Sharing of Personal Information',
    section3Body:
      'The Service Provider may share your information with third parties in the ways that are described in this privacy statement.',
    section3_1Li: 'When required by law',
    section3_2Li:
      'When necessary to protect someone’s life, safety, or property',
    section3_3Li:
      'when they believe in good faith that disclosure is necessary to protect their rights, protect your safety or the safety of others, investigate fraud, or respond to a government request;',
    section3_4Li:
      'with their trusted services providers who work on their behalf, do not have an independent use of the information we disclose to them, and have agreed to adhere to the rules set forth in this privacy statement.',
    section3_3Title: '3.1 Use of External Services',
    section3_3Body:
      'The App uses the following external service, which may receive user information as part of its operation. Please review its privacy policy for details:',
    section3_3Firebase: 'Firebase (Google LLC):',
    section3_3FirebaseLink: 'https://policies.google.com/privacy',
    section4Title: '4. Opt-Out Rights',
    section4Body:
      'You can stop all collection of information by the Application easily by uninstalling it. You may use the standard uninstall processes as may be available as part of your mobile device or via the mobile application marketplace or network.',
    section5Title: '5. Data Retention Policy',
    section5Body:
      "The Service Provider will retain User Provided data for as long as you use the Application and for a reasonable time thereafter. If you'd like them to delete User Provided Data that you have provided via the Application, please contact them at brownpowder@gmail.com and they will respond in a reasonable time.",
    section6Title: '6. Children',
    section6Body:
      "The Service Provider does not use the Application to knowingly solicit data from or market to children under the age of 13. The Service Provider encourage parents and legal guardians to monitor their children's Internet usage and to help enforce this Policy by instructing their children never to provide personally identifiable information through the Application and/or Services without their permission. If you have reason to believe that a child has provided personally identifiable information to the Service Provider through the Application and/or Services, please contact the Service Provider (brownpowder@gmail.com) so that they will be able to take the necessary actions.",
    section7Title: '7. Security',
    section7Body:
      'The Service Provider is concerned about safeguarding the confidentiality of your information. The Service Provider provides physical, electronic, and procedural safeguards to protect information the Service Provider processes and maintains.',
    section8Title: '8. Changes',
    section8Body:
      'This Privacy Policy may be updated from time to time for any reason. The Service Provider will notify you of any changes to the Privacy Policy by updating this page with the new Privacy Policy. You are advised to consult this Privacy Policy regularly for any changes, as continued use is deemed approval of all changes.',
    section9Title: '9. Your Consent',
    section9Body:
      'By using the Application, you are consenting to the processing of your information as set forth in this Privacy Policy now and as amended by us.',
    section10Title: '10. Governing Law and Jurisdiction',
    section10Body:
      'This Policy shall be governed by the laws of Japan. Any and all disputes arising out of or in connection with this Policy shall be submitted to the exclusive jurisdiction of the Tokyo District Court in the first instance.',
    section11Title: '11. Feedback Feature',
    section11Body: (
      <>
        <p
          className="privacypolicy_contents_body"
          style={{ marginBottom: '1rem' }}
        >
          This app offers a feature allowing users to voluntarily report word issues for quality improvement. Using this feature collects and sends the following information:
        </p>
        <ul
          className="privacypolicy_contents_ul"
          style={{ listStyle: 'disc', paddingLeft: '20px' }}
        >
          <li
            className="privacypolicy_contents_ul_li"
            style={{ marginBottom: '0.5rem' }}
          >
            <strong>Information Collected:</strong> Reported word details, issue type, optional text, report timestamp, app version.
          </li>
          <li
            className="privacypolicy_contents_ul_li"
            style={{ marginBottom: '0.5rem' }}
          >
            <strong>Purpose of Use:</strong> To improve content accuracy and enhance our services.
          </li>
          <li className="privacypolicy_contents_ul_li">
            <strong>Data Transmission and Storage:</strong> Reports are sent to and stored on Google's Firebase (Firestore). For more details, please see Google's Privacy Policy:{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://policies.google.com/privacy
            </a>
          </li>
        </ul>
      </>
    ),
    section12Title: '12. Contact Us',
    section12Body:
      'If you have any questions regarding privacy while using the Application, or have questions about the practices, please contact the Service Provider via email at brownpowder@gmail.com.',
  };

  const currentContent = lang === 'jp' ? jpContent : enContent;

  return (
    <div className="privacypolicy" style={{ width: '100%', minHeight: '100vh', backgroundColor: '#fff', padding: '4rem 2rem' }}>
      <ToTop />
      <div
        className="privacypolicy_contents"
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          fontFamily: 'sans-serif',
        }}
      >
        <div style={{ textAlign: 'right', marginBottom: '2rem' }}>
          <LangButton
            onClick={() => setLang('jp')}
            isActive={lang === 'jp'}
            style={{ marginRight: '10px' }}
          >
            日本語
          </LangButton>
          <LangButton onClick={() => setLang('en')} isActive={lang === 'en'}>
            English
          </LangButton>
        </div>

        <PolicyHeader
          title={currentContent.title}
          subTitle={currentContent.subTitle}
          lastUpdate={currentContent.lastUpdate}
        />

        <p
          className="privacypolicy_contents_body"
          style={{ marginBottom: '2.5rem' }}
        >
          {currentContent.introduction}
        </p>

        <Section title={currentContent.section1Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section1Body}
          </p>
          <h3 className="privacypolicy_contents_h3">
            {currentContent.section1_1Title}
          </h3>
          <p className="privacypolicy_contents_body">
            {currentContent.section1_1Content1}
          </p>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_1Li1}
            </li>
          </ul>
          <h3 className="privacypolicy_contents_h3">
            {currentContent.section1_2Title}
          </h3>
          <p className="privacypolicy_contents_body">
            {currentContent.section1_2Body}
          </p>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_2Li1}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_2Li2}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_2Li3}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section2Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section2Body}
          </p>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_2Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_3Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_4Li}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section3Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section3Body}
          </p>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_2Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_3Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_4Li}
            </li>
          </ul>
          <h3 className="privacypolicy_contents_h3">
            {currentContent.section3_3Title}
          </h3>
          <p className="privacypolicy_contents_body">
            {currentContent.section3_3Body}
          </p>
          <p className="privacypolicy_contents_body">
            <strong>{currentContent.section3_3Firebase}</strong>:{' '}
            <a
              href={currentContent.section3_3FirebaseLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {currentContent.section3_3FirebaseLink}
            </a>
          </p>
        </Section>

        <Section title={currentContent.section4Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section4Body}
          </p>
        </Section>

        <Section title={currentContent.section5Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section5Body}
          </p>
        </Section>

        <Section title={currentContent.section6Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section6Body}
          </p>
        </Section>

        <Section title={currentContent.section7Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section7Body}
          </p>
        </Section>

        <Section title={currentContent.section8Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section8Body}
          </p>
        </Section>

        <Section title={currentContent.section9Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section9Body}
          </p>
        </Section>

        <Section title={currentContent.section10Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section10Body}
          </p>
        </Section>

        <Section title={currentContent.section11Title}>
          {currentContent.section11Body}
        </Section>

        {currentContent.section12Title && (
          <Section title={currentContent.section12Title}>
            <p className="privacypolicy_contents_body">
              {currentContent.section12Body}
            </p>
          </Section>
        )}
      </div>
    </div>
  );
};

export default EtanPrivacypolicy;
